import { useMonaco } from '@monaco-editor/react';
import { MaximizeBIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { clone } from '@prophecy/utils/data';
import { useForceBlurOnUnmount } from '@prophecy/utils/react/focus';
import { useDebounce, useDependantState, usePersistentCallback, useTimedFlag } from '@prophecy/utils/react/hooks';
import { clamp, noop } from 'lodash-es';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { useSuggestions, usePlaceholderSuggestion } from '../../Editor/editorHooks';
import { useTagDecorations } from '../../Editor/editorTags';
import { Monaco, IStandaloneCodeEditor } from '../../Editor/types';
import { remeasureFontOnLoad, withMonacoLoader } from '../../Editor/utils';
import { StyledLabel } from '../../Input/styled';
import { useInputActionBarOpenState } from '../../InputActionBarPopup/InputActionBarPopup';
import { Stack } from '../../Layout';
import { renderWithTooltip, Tooltip } from '../../Tooltip';
import { LabelWithHint } from '../../Typography/LabelWithHint';
import { ExpressionBoxProps } from '../types';
import {
  MINIMUM_MONACO_HEIGHT,
  MAX_LINE,
  EXPANDABLE_MAX_LINE,
  options,
  LANGUAGE_ICON_MAP,
  CodeWrapper,
  StyledCodeBlock,
  ExpressionActionsWrapper,
  StyledExpressionButton,
  EditorWrapper,
  ExpressionBoxOverflowWidgetsZIndex,
  linesToHeight
} from './components';
import {
  EXPRESSION_ACTION_WRAPPER_CLS,
  EXPRESSION_BOX_CODE_BLOCK_CLS,
  EXPRESSION_BOX_EDITOR_CLS,
  EXPRESSION_BOX_EXPAND_EDITOR_LABEL,
  EXPRESSION_BOX_VALUE_ATTR
} from './constants';
import { StyledEditor } from './StyledEditor';

const FakeLine = styled.span`
  position: absolute;
  right: 0px;
  width: 60px;
`;
export default withMonacoLoader(function ExpressionBox({
  value = '',
  title,
  language,
  hint,
  tooltip,
  delay = 250,
  onChange,
  onBlur,
  onFocus,
  gap,
  suggestions,
  getInlineSuggestion,
  errors,
  showScrollBar,
  readOnly = false,
  expandable = false,
  expressionActions,
  placeholder,
  placeholderSuggestion,
  setPlaceholderSuggestion,
  className,
  singleLineOnBlur,
  maxLines,
  onExpand,
  prepareSuggestions,
  tags,
  fixedOverflowWidgets,
  fontSize,
  minHeight: propMinHeight = 0,
  wordWrap = false
}: ExpressionBoxProps) {
  const editorRef = useRef<IStandaloneCodeEditor | undefined>();
  const expressionBoxWrapper = useRef<HTMLDivElement | null>(null);
  const monaco = useMonaco();
  const editor = editorRef.current;
  const isInputActionBarOpen = useInputActionBarOpenState();
  /**
   * keep local optimistic state. If we don't case like this will fail due to inner implementation
   * Type:
   * a -> b -> Backspace -> b
   * The output should be `ab` but it will reset to `a`.
   */
  const [_state, setState] = useDependantState((value) => value?.toString() || '', [value]);

  const _handleChange = (newValue: string | undefined) => {
    // when a number is entered in text input, it gets converted to Number
    setState(newValue?.toString() || '');
    slowOnChange(newValue);
  };

  // handle tag decoration
  const [state, handleChange, removeDecoration, hasTags] = useTagDecorations({
    editor,
    monaco,
    tags,
    value: _state,
    readOnly,
    onChange: _handleChange,
    allowTagDropdownOverflow: true
  });

  const valueHasTags = _state !== state;

  const codeBlockState = state || placeholder || '';

  // Resolve the visible-row cap: if the consumer explicitly passes `maxLines`
  // we always honor it. Otherwise fall back to the `expandable`-aware default
  // (a small 4-line preview when the maximize button is shown — to give users
  // a reason to expand — and the larger generic cap otherwise). This preserves
  // legacy behavior for callers that pair `expandable` with the implicit
  // default while still letting callers opt into a custom cap regardless.
  const maxLine = maxLines ?? (expandable ? EXPANDABLE_MAX_LINE : MAX_LINE);

  const codeLines = state.split('\n').length;
  const isLargeCode = codeLines > 50;

  const renderCodeBlockOnBlur = !(isLargeCode || valueHasTags || isInputActionBarOpen);

  const staticMinHeight = Math.max(propMinHeight, MINIMUM_MONACO_HEIGHT);
  const minHeight = Math.max(staticMinHeight, linesToHeight((placeholder || '').split('\n').length));
  const maxHeight = linesToHeight(maxLine);
  const calculatedHeight = clamp(linesToHeight(codeLines), minHeight, maxHeight);

  const actionBoxRef = useRef<HTMLDivElement | null>(null);

  // When `fixedOverflowWidgets` is true, Monaco sets its overflow widgets
  // (suggestion popup, hover, parameter hints, …) to `position: fixed`. That's
  // meant to escape the editor's own bounds, but a CSS `transform` on any
  // ancestor (e.g. our Dialog uses `transform: translateY(-50%)` for vertical
  // centering) creates a new containing block, so the "fixed" widget is
  // actually positioned relative to — and clipped by — that ancestor instead
  // of the viewport. The result is suggestions getting trimmed inside dialogs
  // *and* spurious horizontal scrollbars as Monaco's layout math diverges
  // from where the widget actually paints.
  //
  // Mounting Monaco's overflow widgets in a sibling-of-`<body>` container
  // guarantees the widget tree has no transformed ancestor, so `position:
  // fixed` truly resolves against the viewport. We lazily create one such
  // container per ExpressionBox instance and let Monaco own its contents via
  // the `overflowWidgetsDomNode` construction option.
  const overflowWidgetsDomNodeRef = useRef<HTMLDivElement | null>(null);
  // Guard `document.createElement` for SSR — matches the existing pattern in
  // ui-v3 (see Spotlight components). The render path runs on the server in
  // SSR consumers; the body-append `useEffect` below already only runs on the
  // client, but the synchronous node creation also needs guarding.
  if (fixedOverflowWidgets && !overflowWidgetsDomNodeRef.current && typeof document !== 'undefined') {
    const node = document.createElement('div');
    // Mark the node so the global z-index style (see
    // `ExpressionBoxOverflowWidgetsZIndex`) lifts it above any host portal/
    // dialog. Adding `monaco-editor` ensures Monaco's own scoped CSS still
    // matches the widgets rendered inside.
    node.className = 'monaco-editor monaco-editor-overflow-widgets-root';
    overflowWidgetsDomNodeRef.current = node;
  }
  useEffect(() => {
    const node = overflowWidgetsDomNodeRef.current;
    if (!node) return;
    document.body.appendChild(node);
    return () => {
      node.remove();
    };
  }, []);

  const _options = {
    ...clone(options),
    readOnly,
    scrollBeyondLastColumn: 10,
    revealHorizontalRightPadding: expandable ? 50 : 30,
    fixedOverflowWidgets,
    ...(overflowWidgetsDomNodeRef.current ? { overflowWidgetsDomNode: overflowWidgetsDomNodeRef.current } : {})
  };

  if (wordWrap) {
    _options.wordWrap = 'on';
    _options.wrappingIndent = 'same';
  }

  if (fontSize) {
    _options.fontSize = fontSize;
  }

  if (hasTags) {
    _options.unicodeHighlight = {
      allowedCharacters: {
        '\u200B': true as const
      }
    };
  }

  const [inFocus, setFocus] = useState(false);
  const [height, setHeight] = useState(calculatedHeight);

  let showScrollBars = codeLines > maxLine || showScrollBar;
  if (showScrollBars && _options.scrollbar) {
    _options.scrollbar.vertical = 'auto';
    // With wordWrap on, content never overflows horizontally — keep the
    // horizontal scrollbar fully hidden so a single keystroke can't trigger an
    // empty-track scrollbar at the bottom of the editor.
    _options.scrollbar.horizontal = wordWrap ? 'hidden' : 'auto';
  }

  const [editorLoaded, toggleEditorLoaded] = useState(false);

  const blurCallbackRef = useRef<ExpressionBoxProps['onBlur']>();
  blurCallbackRef.current = onBlur;
  const slowOnChange = useDebounce(onChange || noop, delay);

  // On blur don't wait for debounce and update the parent with new value
  const handleBlur = usePersistentCallback((e: React.FocusEvent<HTMLDivElement>) => {
    // user might quickly focus in and then blur before the editor gets loaded
    // in such case we don't need to call onBlur and onChange
    if (editor) {
      const editorValue = removeDecoration((editor as IStandaloneCodeEditor).getValue());
      // cancel debounce
      slowOnChange.cancel();

      // trigger change only if value is not up to date
      if (editorValue !== value) {
        onChange?.(editorValue);
      }

      onBlur?.(editorValue);
    }

    // if the focus is not going to any other element, radix will try to focus trap to containers,
    // to avoid that we need to manually focus the container
    // also add the focus to the element before the editor is removed so focus is not auto shifted the body causing radix to focus trap
    if (e.relatedTarget === null) {
      expressionBoxWrapper.current?.focus();
    } else {
      (e.relatedTarget as HTMLElement)?.focus();
    }

    // reset the focus state and editor
    setFocus(false);

    if (renderCodeBlockOnBlur) {
      toggleEditorLoaded(false);
      editorRef.current = undefined;
    }
  });

  useForceBlurOnUnmount(expressionBoxWrapper, handleBlur);

  // handle focus of editors
  const handleFocus = usePersistentCallback(() => onFocus?.(editor));
  useEffect(() => {
    if (inFocus && editorLoaded && editor) {
      editor.focus();
      handleFocus();
    }
  }, [inFocus, editor, editorLoaded, state, handleFocus]);

  // handle height reset on blur
  useEffect(() => {
    if (!singleLineOnBlur) return;

    setHeight(inFocus ? calculatedHeight : staticMinHeight);
    // we don't want to call effect if calculatedHeight changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inFocus, singleLineOnBlur]);

  useSuggestions(suggestions, getInlineSuggestion, editor, monaco, prepareSuggestions);

  const updateHeight = usePersistentCallback(() => {
    if (!editor) return;

    const contentHeight = clamp(editor.getContentHeight(), minHeight, maxHeight);

    // change the editor container height manually to avoid flicker on enter press
    const editorDomNode = editor.getDomNode();
    if (contentHeight !== height && editorDomNode) {
      editor.layout({
        width: editorDomNode.offsetWidth, // as width is required
        height: contentHeight
      });
    }
    setHeight(contentHeight);
  });

  function handleEditorDidMount(editor: IStandaloneCodeEditor, monaco: Monaco) {
    editorRef.current = editor;
    remeasureFontOnLoad(monaco, _options.fontSize);

    setTimeout(() => {
      toggleEditorLoaded(true);
      // wait for 100 ms for editor to be completely ready
    }, 100);

    editor.onDidContentSizeChange(updateHeight);
    if (value?.length) {
      editor?.setPosition({ lineNumber: value.length, column: value.length });
    }
  }

  /**
   *
   * We render code block when expression is not focus
   * We Switch to actual editor on focus to improve rendering performance
   *
   * Also,
   * Always render editor in case of large line of codes, editor is faster than codeblock
   * in such case as it does virtualization internally
   */

  // show language hint
  const [showLanguageHint, setShowLanguageHint] = useTimedFlag(2000);
  useEffect(() => {
    setShowLanguageHint(inFocus);
  }, [inFocus, setShowLanguageHint]);
  const LanguageIcon = LANGUAGE_ICON_MAP[language];

  usePlaceholderSuggestion({
    placeholderSuggestion,
    setPlaceholderSuggestion,
    focused: inFocus,
    onChange,
    editor
  });

  const showPlaceholder = Boolean(!state && placeholder);
  const hasEditor = Boolean(inFocus || !renderCodeBlockOnBlur || editor);

  let editorElem = (
    <CodeWrapper
      {...{ [EXPRESSION_BOX_VALUE_ATTR]: value }}
      ref={expressionBoxWrapper}
      className={errors && errors?.length > 0 ? `${className} has-error` : className}>
      {(!editor && renderCodeBlockOnBlur) || showPlaceholder ? (
        <StyledCodeBlock
          className={EXPRESSION_BOX_CODE_BLOCK_CLS}
          inputSize='m'
          $fontSize={_options.fontSize}
          disabled={readOnly}
          variant={errors && errors?.length > 0 ? 'error' : 'primary'}
          tabIndex={readOnly ? -1 : 0}
          height={height}
          code={codeBlockState}
          hideScrollbar={!showScrollBars}
          onFocus={() => (readOnly ? null : setFocus(true))}
          language={language}
          isPlaceholder={Boolean(!state && placeholder)}
          wrapLines={wordWrap}
        />
      ) : null}

      {hasEditor && (
        <EditorWrapper onBlur={handleBlur} $isEmpty={_state === ''}>
          <StyledEditor
            className={EXPRESSION_BOX_EDITOR_CLS}
            inputSize='m'
            variant={errors && errors?.length > 0 ? 'error' : 'primary'}
            height={height}
            language={language}
            value={state}
            onMount={handleEditorDidMount}
            options={_options}
            onChange={handleChange}
            loading={null}
          />
        </EditorWrapper>
      )}
      {/*
      rendering a invisible line(FakeLine) so hotspot can track action even if actions are not visible (not focused/hovered)
      */}
      <FakeLine data-component='fake-action-container' />
      <ExpressionActionsWrapper
        direction='horizontal'
        hasEditor={!!hasEditor}
        gap={theme.spaces.x4}
        alignY='center'
        align='space-between'
        className={EXPRESSION_ACTION_WRAPPER_CLS}
        ref={actionBoxRef}>
        {showLanguageHint && LanguageIcon && <LanguageIcon className='expression-language-icon' />}
        {expressionActions}
        {expandable && (
          <Tooltip title={EXPRESSION_BOX_EXPAND_EDITOR_LABEL}>
            <StyledExpressionButton
              disablePointerFocus
              aria-label={EXPRESSION_BOX_EXPAND_EDITOR_LABEL}
              size='s'
              variant='tertiaryGray'
              icon={<MaximizeBIcon type='default' />}
              onMouseDown={(e: React.MouseEvent) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              onClick={(_e: React.MouseEvent) => {
                onExpand?.();
              }}
              data-test-id='expression-box-expand-button'
            />
          </Tooltip>
        )}
      </ExpressionActionsWrapper>
    </CodeWrapper>
  );

  let titleUI = title ? <StyledLabel>{title}</StyledLabel> : null;

  if (hint && title) {
    titleUI = (
      <LabelWithHint level='xs' hint={hint}>
        {titleUI}
      </LabelWithHint>
    );
  }

  if (tooltip) {
    editorElem = <>{renderWithTooltip(editorElem, tooltip)}</>;
  }

  const content = title ? (
    <Stack gap={gap ? gap : theme.spaces.x6}>
      {titleUI}
      {editorElem}
    </Stack>
  ) : hint ? (
    <LabelWithHint hint={hint}>{editorElem}</LabelWithHint>
  ) : (
    editorElem
  );

  // `ExpressionBoxOverflowWidgetsZIndex` is a `createGlobalStyle` so it only
  // emits a single `<style>` tag regardless of how many ExpressionBoxes are
  // mounted. Rendering it unconditionally keeps Monaco's `fixedOverflowWidgets`
  // root above any host portal/dialog (see component definition for details).
  return (
    <>
      <ExpressionBoxOverflowWidgetsZIndex />
      {content}
    </>
  );
});
